import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Users } from 'lucide-react'
import type { PageMeta } from '../../../lib/permissions/pageMeta'
import { PageHeader } from '../../../components/ui/PageHeader'
import { EmptyState } from '../../../components/ui/EmptyState'
import { Spinner } from '../../../components/ui/Spinner'
import { SearchInput } from '../../../components/ui/SearchInput'
import { Pagination } from '../../../components/ui/Pagination'
import { DateRangePicker } from '../../../components/ui/DateRangePicker'
import { ExportButton } from '../../../components/common/ExportButton'
import { HeaderFilter } from '../../../components/ui/HeaderFilter'
import { HeaderRangeFilter } from '../../../components/ui/HeaderRangeFilter'
import { SummaryStrip } from '../../../components/ui/SummaryStrip'
import { SnCell, SnHeader } from '../../../components/ui/SnCell'
import { useDateRange } from '../../../hooks/useDateRange'
import { useDebounce } from '../../../hooks/useDebounce'
import { useCurrency } from '../../../hooks/useCurrency'
import { useAuthStore } from '../../../store/authStore'
import { shortDate } from '../../../lib/paymentReport/format'
import { cn } from '../../../utils/cn'
import {
  fetchCustomerPaymentsPage,
  fetchCustomerPaymentsTotal,
  fetchCustomerPaymentsExport,
  type CustomerPaymentFilters,
} from '../../../lib/paymentReport/customerPayments'
import {
  fetchCustomerNameOptions,
  METHOD_OPTIONS,
  CUSTOMER_STATUS_OPTIONS,
} from '../../../lib/paymentReport/filterOptions'

const PILL_TABS = ['today', 'week', 'month', 'year', 'custom'] as const

export const pageMeta: PageMeta = {
  key: 'trading.payments.customers',
  label: 'Customer Payments',
  module: 'Trading',
  section: 'Payments',
  description: 'Receipts received from customers',
  defaultPermissions: {
    owner: 'view', admin: 'view', manager: 'view',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

export default function CustomerPaymentsTab() {
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const { format } = useCurrency()
  const dr = useDateRange('month')

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [showVoided, setShowVoided] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Header filters (Feature 4).
  const [fCustomer, setFCustomer] = useState<string[]>([])
  const [fMethod, setFMethod] = useState<string[]>([])
  const [fStatus, setFStatus] = useState<string[]>([])
  const [amtMin, setAmtMin] = useState<number | null>(null)
  const [amtMax, setAmtMax] = useState<number | null>(null)

  const filtersActive =
    fCustomer.length > 0 || fMethod.length > 0 || fStatus.length > 0 || amtMin != null || amtMax != null
  function clearFilters() {
    setFCustomer([]); setFMethod([]); setFStatus([]); setAmtMin(null); setAmtMax(null)
  }

  const filters = useMemo<CustomerPaymentFilters>(
    () => ({
      from: dr.from, to: dr.to, search: debouncedSearch.trim(), showVoided,
      customerIds: fCustomer, methods: fMethod, statuses: fStatus,
      amountMin: amtMin, amountMax: amtMax,
    }),
    [dr.from, dr.to, debouncedSearch, showVoided, fCustomer, fMethod, fStatus, amtMin, amtMax],
  )

  useEffect(() => { setPage(1) }, [filters])

  const { data: customerOptions = [] } = useQuery({
    queryKey: ['customer-name-options', tid],
    enabled: !!tid,
    queryFn: () => fetchCustomerNameOptions(tid),
    staleTime: 5 * 60 * 1000,
  })

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['customer-payments-page', tid, filters, page, pageSize],
    enabled: !!tid,
    queryFn: () => fetchCustomerPaymentsPage(tid, filters, { page, pageSize }),
    staleTime: 30 * 1000,
  })
  const rows = pageData?.rows ?? []
  const totalCount = pageData?.count ?? 0

  const { data: periodTotal = 0 } = useQuery({
    queryKey: ['customer-payments-total', tid, filters],
    enabled: !!tid,
    queryFn: () => fetchCustomerPaymentsTotal(tid, filters),
    staleTime: 30 * 1000,
  })

  return (
    <div className="space-y-4">
      <PageHeader
        title="Customer Payments"
        subtitle="Receipts received from customers"
        actions={
          <ExportButton
            filename={`customer-payments-${filters.from}-to-${filters.to}`}
            data={rows.map(r => ({ id: r.id }))}
            fetchData={() => fetchCustomerPaymentsExport(tid, filters)}
            columns={[
              { key: 'date', header: 'Date', isDate: true },
              { key: 'customer', header: 'Customer' },
              { key: 'method', header: 'Method' },
              { key: 'amount', header: 'Amount', isCurrency: true },
              { key: 'sale_reference', header: 'Sale Reference' },
              { key: 'journal', header: 'Journal' },
              { key: 'status', header: 'Status' },
            ]}
          />
        }
      />

      <SummaryStrip
        count={totalCount}
        amount={format(periodTotal)}
        filtersActive={filtersActive}
        onClearFilters={clearFilters}
      />

      {/* Controls: date pills · search · show-voided */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <DateRangePicker {...dr} tabs={[...PILL_TABS]} />
        <div className="flex items-center gap-3">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            placeholder="Customer or note…"
            containerClassName="w-full sm:w-64"
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-400"
              checked={showVoided}
              onChange={e => setShowVoided(e.target.checked)}
            />
            Show voided
          </label>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Users className="h-8 w-8" />}
          title="No customer payments"
          description="No receipts from customers in this period. Try widening the date range or clearing filters."
        />
      ) : (
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
                  <SnHeader />
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">
                    <HeaderFilter label="Customer" options={customerOptions} selected={fCustomer} onChange={setFCustomer} />
                  </th>
                  <th className="px-4 py-3">
                    <HeaderFilter label="Method" options={METHOD_OPTIONS} selected={fMethod} onChange={setFMethod} />
                  </th>
                  <th className="px-4 py-3 text-right">
                    <HeaderRangeFilter
                      label="Amount"
                      min={amtMin}
                      max={amtMax}
                      onChange={({ min, max }) => { setAmtMin(min); setAmtMax(max) }}
                    />
                  </th>
                  <th className="px-4 py-3">Sale Reference</th>
                  <th className="px-4 py-3">Journal</th>
                  <th className="px-4 py-3">
                    <HeaderFilter label="Status" options={CUSTOMER_STATUS_OPTIONS} selected={fStatus} onChange={setFStatus} align="right" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                    <SnCell page={page} pageSize={pageSize} index={i} />
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600">{shortDate(r.date)}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{r.customerName}</td>
                    <td className="px-4 py-3 capitalize text-gray-600">{r.method ?? '—'}</td>
                    <td className={cn('px-4 py-3 text-right font-semibold whitespace-nowrap', r.isVoided ? 'text-gray-400 line-through' : 'text-gray-900')}>
                      {format(r.amount)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{r.saleRef ?? '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500" title={r.journalId ?? undefined}>
                      {r.journalNumber ?? (r.journalId ? r.journalId.slice(0, 8) : '—')}
                    </td>
                    <td className="px-4 py-3">
                      {r.isVoided
                        ? <span className="badge badge-red">Voided</span>
                        : r.isDetached
                          ? <span className="badge badge-orange">Detached</span>
                          : <span className="badge badge-green">Posted</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between border-t border-gray-100 px-4 py-2 text-xs text-gray-400">
            <span>{totalCount} payment{totalCount !== 1 ? 's' : ''}</span>
            <span>Total (excl. voided): <strong className="text-gray-700">{format(periodTotal)}</strong></span>
          </div>

          <div className="px-4 pb-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPageSizeChange={s => { setPageSize(s); setPage(1) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
